import { useState } from "react";

const KPIS = [
  { label: "Suppliers", value: "312", color: "#7c3aed" },
  { label: "Categories (L4)", value: "48", color: "#8b5cf6" },
  { label: "Regions", value: "8", color: "#a78bfa" },
  { label: "Plants", value: "3", color: "#c4b5fd" },
];

const SUPPLIERS: [string, number][] = [
  ["Castrol", 9.3],
  ["Magna International", 7.3],
  ["Autoliv", 4.7],
  ["Continental", 4.3],
  ["Tenneco", 3.8],
  ["Denso", 2.9],
  ["SKF", 2.4],
  ["Bosch", 2.1],
  ["Lear", 1.8],
  ["Mann+Hummel", 1.5],
];
const OTHER_SUPPLIERS = 8.2;

const PARETO: [string, number, string][] = [
  ...SUPPLIERS.map(([n, v]) => [n, v, `$${v}M`] as [string, number, string]),
  ["Hutchinson", 1.2, "$1.2M"],
  ["NSK", 0.963, "$0.963M"],
];

const GEO = [
  { label: "United States", pct: 24, color: "#ec1c8c" },
  { label: "United Kingdom", pct: 19, color: "#c4b5fd" },
  { label: "Germany", pct: 17, color: "#f59e0b" },
  { label: "Canada", pct: 17, color: "#10b981" },
  { label: "India", pct: 10, color: "#4c1d95" },
  { label: "Japan", pct: 8, color: "#0ea5e9" },
  { label: "Mexico", pct: 3, color: "#8b5cf6" },
  { label: "Other", pct: 3, color: "#d1d5db" },
];

const LEVELS = [
  { title: "Level 1", rows: [["Vehicles & accessories", "$22.5M"], ["Fuels & lubricants", "$9.3M"], ["Power generation", "$5.4M"], ["IT & telecom", "$4.9M"], ["Electrical", "$2.4M"]] },
  { title: "Level 2", rows: [["Transportation components", "$18.7M"], ["Lubricants & oils", "$9.3M"], ["IT components", "$4.9M"], ["Bearings & transmission", "$3M"], ["Power sources", "$2.4M"]] },
  { title: "Level 3", rows: [["Lubricating preparations", "$9.3M"], ["Chassis components", "$5.8M"], ["Braking systems", "$4.2M"], ["Tire systems", "$2.7M"], ["Safety systems", "$1.8M"]] },
  { title: "Level 4", rows: [["Engine oil", "$7.5M"], ["Chassis brackets", "$4.9M"], ["Brake discs", "$2.5M"], ["Wheel bearings", "$2.1M"], ["Cabin filters", "$1.6M"]] },
];

function GeoDonut() {
  const r = 56;
  const circ = 2 * Math.PI * r;
  const total = GEO.reduce((s, g) => s + g.pct, 0);
  let offset = 0;
  return (
    <svg className="sd-donut" viewBox="0 0 160 160" role="img" aria-label="Spend by geography">
      {GEO.map(({ label, pct, color }) => {
        const dash = (pct / total) * circ;
        const rot = (offset / total) * 360 - 90;
        offset += pct;
        return (
          <circle
            key={label}
            cx="80"
            cy="80"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="24"
            strokeDasharray={`${dash} ${circ - dash}`}
            transform={`rotate(${rot} 80 80)`}
          />
        );
      })}
      <text x="80" y="76" textAnchor="middle" fontSize="9" fill="#9ca3af">Total</text>
      <text x="80" y="94" textAnchor="middle" fontSize="15" fontWeight="700" fill="#1e1b4b">$48.2M</text>
    </svg>
  );
}

function ParetoChart() {
  const w = 1000;
  const h = 300;
  const padL = 150;
  const padR = 60;
  const padT = 26;
  const padB = 24;
  const plotW = w - padL - padR;
  const rowH = (h - padT - padB) / PARETO.length;
  const total = 48.2;
  const maxX = 10;
  const bx = (v: number) => padL + (v / maxX) * plotW;
  let cum = 0;
  const pts = PARETO.map(([, v], i) => {
    cum += v;
    return [padL + (cum / total) * plotW, padT + rowH * i + rowH / 2] as [number, number];
  });
  const shade = (i: number) => `hsl(${262 - i * 0.5}, ${70 - i * 1.5}%, ${35 + i * 3}%)`;

  return (
    <svg className="sm-chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Supplier concentration Pareto">
      {[0, 25, 50, 75, 100].map((p) => (
        <g key={p}>
          <line x1={padL + (p / 100) * plotW} x2={padL + (p / 100) * plotW} y1={padT - 6} y2={h - padB} stroke="#e5e7eb" strokeDasharray="4 4" />
          <text x={padL + (p / 100) * plotW} y={12} textAnchor="middle" fontSize="12" fill="#9ca3af">{`${p}%`}</text>
        </g>
      ))}
      {[0, 3, 6, 10].map((v) => (
        <text key={v} x={bx(v)} y={h - 6} textAnchor="middle" fontSize="12" fill="#9ca3af">{`$${v}M`}</text>
      ))}
      {PARETO.map(([name, v, label], i) => (
        <g key={name}>
          <rect x={padL} y={padT + rowH * i + 2} width={bx(v) - padL} height={rowH - 4} rx="4" fill={shade(i)} />
          <text x={padL - 10} y={padT + rowH * i + rowH / 2 + 4} textAnchor="end" fontSize="12" fill="#6d28d9">{name}</text>
          <text x={bx(v) + 8} y={padT + rowH * i + rowH / 2 + 4} fontSize="12" fill="#4b5563">{label}</text>
        </g>
      ))}
      <path d={pts.map(([x, y], i) => `${i ? "L" : "M"}${x} ${y}`).join(" ")} fill="none" stroke="#ec1c8c" strokeWidth="2" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.5" fill="#ec1c8c" />
      ))}
    </svg>
  );
}

export function SpendDimensionsPanel() {
  const [activeLevel, setActiveLevel] = useState(0);
  const maxSupplier = SUPPLIERS[0][1];

  return (
    <div className="sm-panel">
      <div className="sm-kpi-grid">
        {KPIS.map(({ label, value, color }) => (
          <div key={label} className="sm-kpi" style={{ borderColor: color }}>
            <p className="sm-kpi__label">{label}</p>
            <p className="sm-kpi__value sd-kpi__value">{value}</p>
            <p className="sm-kpi__sub">in scope</p>
          </div>
        ))}
      </div>

      <div className="si-grid-2">
        <div className="si-card sm-card">
          <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
            <div>
              <p className="sm-card__title sm-card__title--tight">Spend by Supplier</p>
              <p className="sm-card__subtitle">Top 10 suppliers by spend · click to open a profile</p>
            </div>
            <span className="sm-tag">$48.2M</span>
          </div>
          <div className="sd-bars">
            {SUPPLIERS.map(([name, v]) => (
              <div key={name} className="sd-bar">
                <button type="button" className="sd-bar__label">{name}</button>
                <div className="sm-geo__track">
                  <div className="sm-geo__fill" style={{ width: `${(v / maxSupplier) * 100}%` }} />
                </div>
                <p className="sd-bar__value">${v}M</p>
              </div>
            ))}
            <div className="sd-bar">
              <p className="sd-bar__label sd-bar__label--muted">All other suppliers (43)</p>
              <div className="sm-geo__track">
                <div className="sm-geo__fill" style={{ width: `${(OTHER_SUPPLIERS / maxSupplier) * 100}%` }} />
              </div>
              <p className="sd-bar__value">${OTHER_SUPPLIERS}M</p>
            </div>
          </div>
        </div>

        <div className="si-card sm-card">
          <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
            <div>
              <p className="sm-card__title sm-card__title--tight">Spend by Geography</p>
              <p className="sm-card__subtitle sm-card__subtitle--muted">Share of spend by supplier region</p>
            </div>
            <span className="sm-tag">8 regions</span>
          </div>
          <div className="sd-geo">
            <GeoDonut />
            <div className="sd-geo__legend">
              {GEO.map(({ label, pct, color }) => (
                <div key={label} className="sd-geo__item">
                  <span className="sd-geo__swatch" style={{ background: color }} />
                  <span className="sd-geo__label">{label}</span>
                  <strong>{pct}%</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="si-card sm-card">
        <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
          <div>
            <p className="sm-card__title sm-card__title--tight">Category Drilldown, L1 to L4</p>
            <p className="sm-card__subtitle">Click any node to drill into the next level</p>
          </div>
          <span className="sm-tag">Classification face</span>
        </div>
        <div className="sd-levels">
          {LEVELS.map(({ title, rows }, li) => (
            <div key={title} className={`sd-level${li === activeLevel ? " sd-level--active" : ""}`}>
              <p className="sd-level__title">{title}</p>
              {rows.map(([name, val]) => (
                <button
                  key={name}
                  type="button"
                  className="sd-level__row"
                  onClick={() => setActiveLevel(Math.min(li + 1, LEVELS.length - 1))}
                >
                  <span>{name}</span>
                  <strong>{val}</strong>
                </button>
              ))}
            </div>
          ))}
        </div>
        <p className="sm-chart__note sd-note">
          Engine oil ($7.5M) is the largest L4 line; lubricants roll up to $9.3M at L2. Classification confidence averages 89%.
        </p>
      </div>

      <div className="si-card sm-card">
        <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
          <div>
            <p className="sm-card__title sm-card__title--tight">Supplier Concentration, Pareto</p>
            <p className="sm-card__subtitle sm-card__subtitle--muted">Bars = spend, line = cumulative share</p>
          </div>
          <span className="sm-tag">Top 12 = 88%</span>
        </div>
        <ParetoChart />
        <div className="sd-chips">
          {PARETO.map(([name]) => (
            <button key={name} type="button" className="sd-chip">{name}</button>
          ))}
        </div>
        <p className="sm-chart__note sd-note">
          Top 3 suppliers carry 44% of spend; the curve flattens fast, the long tail beyond rank 12 is the consolidation
          target. Click any supplier to open its profile.
        </p>
      </div>
    </div>
  );
}
